import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FreeGuideSignup from "@/components/FreeGuideSignup";
import QuickConsultForm from "@/components/forms/QuickConsultForm";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "kcc_exit_offer_seen";

/** Routes where an interruption would hurt more than it helps. */
const SUPPRESSED_PREFIXES = [
  "/admin",
  "/.lovable",
  "/contact",
  "/care-request",
  "/consultation",
  "/careers",
  "/newsletter",
  "/unsubscribe",
];
const SUPPRESSED_MATCHES = ["/thank-you"];

/**
 * A single, once-per-visitor offer shown when someone is about to leave without
 * taking any next step. Desktop triggers on cursor exit toward the tab bar;
 * touch devices trigger after meaningful dwell + scroll, since there is no
 * mouseout signal. Offers two low-pressure paths: the free Emergency Info
 * Sheet, or a short private consultation request.
 */
const ExitIntentOffer = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("guide");

  const suppressed =
    SUPPRESSED_PREFIXES.some((p) => pathname.startsWith(p)) ||
    SUPPRESSED_MATCHES.some((p) => pathname.includes(p));

  useEffect(() => {
    if (suppressed) return;
    if (typeof window === "undefined") return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY)) return;
    } catch {
      return;
    }

    let fired = false;
    const fire = (trigger: string) => {
      if (fired) return;
      fired = true;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      } catch {
        /* private mode — show once for this session only */
      }
      setOpen(true);
      trackEvent("exit_intent_shown", { trigger, page_path: window.location.pathname });
      cleanup();
    };

    const onMouseOut = (event: MouseEvent) => {
      if (event.relatedTarget) return;
      if (event.clientY > 8) return;
      fire("mouse_exit");
    };

    // Touch fallback: engaged but idle-ish visitor deep in the page.
    // Only run on touch devices; desktop uses mouse-out exit intent only.
    const isTouch = window.matchMedia("(hover: none)").matches;
    let dwell: number | undefined;
    if (isTouch) {
      dwell = window.setTimeout(() => {
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - window.innerHeight;
        const percent = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 0;
        if (percent >= 55) fire("dwell_scroll");
      }, 45000);
    }

    if (!isTouch) document.addEventListener("mouseout", onMouseOut);

    function cleanup() {
      document.removeEventListener("mouseout", onMouseOut);
      if (dwell) window.clearTimeout(dwell);
    }
    return cleanup;
  }, [pathname, suppressed]);

  const handleChange = (next: boolean) => {
    setOpen(next);
    if (!next) trackEvent("exit_intent_dismissed", { page_path: window.location.pathname, tab });
  };

  const handleTab = (next: string) => {
    setTab(next);
    trackEvent("exit_intent_tab", { tab: next, page_path: window.location.pathname });
  };

  return (
    <Dialog open={open} onOpenChange={handleChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto">
        <div className="px-6 pt-6">
          <DialogTitle className="text-lg font-heading font-semibold">
            Before you go — take one useful thing with you
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm">
            No cost, no obligation. Choose whichever is more helpful right now.
          </DialogDescription>
        </div>
        <Tabs value={tab} onValueChange={handleTab} className="p-4 md:p-5">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="guide">Free info sheet</TabsTrigger>
            <TabsTrigger value="consult">Talk with us</TabsTrigger>
          </TabsList>
          <TabsContent value="guide" className="mt-4">
            <FreeGuideSignup source="exit_intent" className="border-0 p-0" />
          </TabsContent>
          <TabsContent value="consult" className="mt-4">
            <QuickConsultForm
              source="exit_intent_consult"
              className="border-0 p-0"
              compact
              heading="A 15-minute private conversation"
              intro="Leave your name and the best way to reach you — we'll follow up with practical next steps for your family."
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentOffer;
